import { z } from 'zod';

import { CREATE_PLACEMENT_KEY_PATTERN } from '~/lib/ai/keyFormat';
import { type FieldErrors, fieldErrors } from '~/lib/validation';
import type {
  ChannelMessage,
  EmailMessage,
  NotificationChannel,
  SendMessageRequest,
  Template,
  TemplateMessage,
  TemplateStatus,
  UpdateTemplateRequest,
} from '~/modules/query/notification/types';

export const TEMPLATE_CHANNELS: readonly NotificationChannel[] = ['email', 'sms', 'browser'];

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  browser: 'Browser',
};

/** `subject` and `from` are only sent for email; the other channels leave them blank. */
export interface ChannelFormValues {
  body: string;
  /** Email only: a service-design template id; set, it replaces the body. Blank = none. */
  designTemplateId: string;
  subject: string;
  from: string;
  /** JSON text; blank means no sample data. */
  sampleData: string;
}

export interface TemplateFormValues {
  name: string;
  templateKey: string;
  description: string;
  /** Empty = a global template, for every customer. */
  customerIds: string[];
  email: ChannelFormValues;
  sms: ChannelFormValues;
  browser: ChannelFormValues;
}

const emptyChannel = (): ChannelFormValues => ({
  body: '',
  designTemplateId: '',
  subject: '',
  from: '',
  sampleData: '',
});

export const emptyTemplateForm = (): TemplateFormValues => ({
  name: '',
  templateKey: '',
  description: '',
  customerIds: [],
  email: emptyChannel(),
  sms: emptyChannel(),
  browser: emptyChannel(),
});

const sampleDataText = (data: ChannelMessage['sampleData']): string =>
  data ? JSON.stringify(data, null, 2) : '';

function channelToForm(channel: ChannelMessage | EmailMessage | undefined): ChannelFormValues {
  if (!channel) return emptyChannel();
  const email = channel as EmailMessage;
  return {
    body: channel.body ?? '',
    designTemplateId: email.designTemplateId ?? '',
    subject: email.subject ?? '',
    from: email.from ?? '',
    sampleData: sampleDataText(channel.sampleData),
  };
}

export function templateToForm(template: Template): TemplateFormValues {
  return {
    name: template.name,
    templateKey: template.templateKey,
    description: template.description ?? '',
    customerIds: template.customerIds,
    email: channelToForm(template.message.email),
    sms: channelToForm(template.message.sms),
    browser: channelToForm(template.message.browser),
  };
}

/** The key a name derives: lowercase letters and numbers, every other run of characters one `_`. */
export function toTemplateKey(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9.]+/g, '_')
    .replace(/[._]{2,}/g, '_')
    .replace(/^[._]+|[._]+$/g, '');
}

/** A new template's rename: the key follows the name until someone edits the key by hand. */
export function renameTemplate(
  form: Pick<TemplateFormValues, 'name' | 'templateKey'>,
  name: string,
): Pick<TemplateFormValues, 'name' | 'templateKey'> {
  const following = form.templateKey === toTemplateKey(form.name);
  return { name, templateKey: following ? toTemplateKey(name) : form.templateKey };
}

export type SampleDataResult =
  | { ok: true; value: Record<string, unknown> | null }
  | { ok: false; error: string };

/** Blank is no sample data; anything else must be a JSON object. */
export function parseSampleData(text: string): SampleDataResult {
  if (!text.trim()) return { ok: true, value: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Sample data is not valid JSON. Check the braces and quotes.' };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Sample data must be a JSON object, like {"name": "Sam"}.' };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

/** A channel sends when it has a body, or (email) a linked design. */
const isFilled = (channel: ChannelFormValues): boolean =>
  channel.body.trim() !== '' || channel.designTemplateId !== '';

/** A saved message sends on a channel when it has a body, or (email) a linked design. */
export const messageSendsOn = (message: TemplateMessage, channel: NotificationChannel): boolean => {
  const values = message[channel];
  return !!values?.body || (channel === 'email' && !!message.email?.designTemplateId);
};

/** The channels with something to send, in tab order; the rest are dropped from the message. */
export const filledChannels = (form: TemplateFormValues): NotificationChannel[] =>
  TEMPLATE_CHANNELS.filter((channel) => isFilled(form[channel]));

/** The sample data a channel's text parses to; unparseable text counts as none. */
export const channelSampleData = (channel: ChannelFormValues): Record<string, unknown> | null => {
  const result = parseSampleData(channel.sampleData);
  return result.ok ? result.value : null;
};

/** Form values to the API `message`: empty channels dropped, blank optional fields omitted. */
export function toTemplateMessage(form: TemplateFormValues): TemplateMessage {
  const message: TemplateMessage = {};
  for (const channel of filledChannels(form)) {
    const values = form[channel];
    const sampleData = channelSampleData(values);
    const base: ChannelMessage = { body: values.body, ...(sampleData ? { sampleData } : {}) };
    if (channel === 'email') {
      const subject = values.subject.trim();
      const from = values.from.trim();
      const { designTemplateId } = values;
      // A linked design is the body; the hidden editor's text is not sent.
      message.email = {
        ...(designTemplateId ? { designTemplateId } : { body: values.body }),
        ...(sampleData ? { sampleData } : {}),
        ...(subject ? { subject } : {}),
        ...(from ? { from } : {}),
      };
    } else {
      message[channel] = base;
    }
  }
  return message;
}

/**
 * The PUT body for a saved template's edits. Name, description and customers always go (they are
 * not versioned); the message only when it differs from the saved one, since it writes a version.
 */
export function toTemplateUpdate(
  form: TemplateFormValues,
  saved: TemplateFormValues,
): Omit<UpdateTemplateRequest, 'id'> {
  const message = toTemplateMessage(form);
  const changed = JSON.stringify(message) !== JSON.stringify(toTemplateMessage(saved));
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    customerIds: form.customerIds,
    ...(changed ? { message } : {}),
  };
}

export const NO_CHANNEL_MESSAGE = 'Write a message for at least one channel.';

/** `isNew` adds the key rule; on an existing template the key is read-only. */
const channelSchema = z.object({
  body: z.string(),
  designTemplateId: z.string(),
  subject: z.string(),
  from: z.string(),
  sampleData: z.string(),
});

export const templateSchema = (isNew: boolean) =>
  z
    .object({
      name: z.string(),
      templateKey: z.string(),
      description: z.string(),
      customerIds: z.array(z.string()),
      email: channelSchema,
      sms: channelSchema,
      browser: channelSchema,
    })
    .superRefine((form, ctx) => {
      if (!form.name.trim()) {
        ctx.addIssue({ code: 'custom', path: ['name'], message: 'Enter a name.' });
      }
      // The placements key rule. Only a new key is checked: an existing one is read-only.
      if (isNew && !form.templateKey.trim()) {
        ctx.addIssue({ code: 'custom', path: ['templateKey'], message: 'Enter a template key.' });
      } else if (isNew && !CREATE_PLACEMENT_KEY_PATTERN.test(form.templateKey)) {
        ctx.addIssue({
          code: 'custom',
          path: ['templateKey'],
          message: 'Use lowercase letters and numbers, joined by . or _, like welcome_email.',
        });
      }
      if (filledChannels(form).length === 0) {
        ctx.addIssue({ code: 'custom', path: ['channels'], message: NO_CHANNEL_MESSAGE });
      }
      for (const channel of TEMPLATE_CHANNELS) {
        const result = parseSampleData(form[channel].sampleData);
        if (!result.ok) {
          // The whole string is one key: `fieldErrors` reads only the first path segment.
          ctx.addIssue({ code: 'custom', path: [`${channel}.sampleData`], message: result.error });
        }
      }
    });

/** The channels a draft can be test-sent on: the Worker skips browser. */
export const TEST_SEND_CHANNELS: readonly NotificationChannel[] = ['email', 'sms'];

/**
 * A test send of the draft as it stands, unsaved edits included: sent as a custom message (a
 * templateKey would send the live version) through the default provider, with the channel's sample
 * data as the recipient's data, so the Worker fills the placeholders as it would for a real send.
 */
export function toDraftTestRequest(
  form: TemplateFormValues,
  channel: NotificationChannel,
  recipient: string,
): SendMessageRequest {
  const values = form[channel];
  const data = channelSampleData(values);
  const subject = channel === 'email' ? values.subject.trim() : '';
  return {
    channels: [channel],
    content: { ...(subject ? { subject } : {}), message: { [channel]: values.body } },
    recipients: [
      {
        ...(channel === 'email' ? { email: recipient.trim() } : { mobile: recipient.trim() }),
        ...(data ? { data } : {}),
      },
    ],
  };
}

/**
 * A test send of a saved template whose email is a linked design: by templateId, so
 * service-notification fetches the design's HTML, with the sample data as the recipient's data.
 */
export function toDesignTestRequest(
  templateId: string,
  form: TemplateFormValues,
  recipient: string,
): SendMessageRequest {
  const data = channelSampleData(form.email);
  return {
    channels: ['email'],
    content: { templateId },
    recipients: [{ email: recipient.trim(), ...(data ? { data } : {}) }],
  };
}

/** The errors a submit would show, by field; the same call `useFormValidation` makes. */
export const fieldErrorsFor = (form: TemplateFormValues, isNew: boolean): FieldErrors =>
  fieldErrors(templateSchema(isNew), form);

export function templateStatus(latest: Template): TemplateStatus {
  const live = latest.publishedVersion;
  if (live === null) return { kind: 'notLive' };
  const draft = latest.version;
  if (draft !== null && live < draft) return { kind: 'liveWithDraft', live, draft };
  return { kind: 'live', version: draft ?? live };
}

export function templateStatusLabel(status: TemplateStatus): string {
  switch (status.kind) {
    case 'notLive':
      return 'Not live';
    case 'live':
      return 'Live';
    case 'liveWithDraft':
      return `Live v${status.live} · draft v${status.draft}`;
  }
}

export const STATUS_FILTER_OPTIONS: { value: TemplateStatus['kind']; label: string }[] = [
  { value: 'live', label: 'Live' },
  { value: 'liveWithDraft', label: 'Live with a draft' },
  { value: 'notLive', label: 'Not live' },
];

/** The customer filter value that matches templates with no customer. */
export const GLOBAL_CUSTOMER = 'global';

export interface TemplateFilters {
  q: string;
  customerId: string;
  status: string;
}

export function matchesTemplateFilters(template: Template, filters: TemplateFilters): boolean {
  const q = filters.q.trim().toLowerCase();
  if (q && ![template.name, template.templateKey].some((text) => text.toLowerCase().includes(q))) {
    return false;
  }
  if (filters.customerId) {
    const matches =
      filters.customerId === GLOBAL_CUSTOMER
        ? template.customerIds.length === 0
        : template.customerIds.includes(filters.customerId);
    if (!matches) return false;
  }
  return !filters.status || templateStatus(template).kind === filters.status;
}

/** Channel tabs in a stable order for a template's channels; used for the list's Channels column. */
export const templateChannels = (template: Template): NotificationChannel[] =>
  TEMPLATE_CHANNELS.filter((channel) => template.message[channel] !== undefined);
